import { Component, ChangeDetectionStrategy, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FooterComponent } from '../footer/footer.component';
import { AuthHeaderComponent } from '../auth-header/auth-header.component';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterLink, FooterComponent, AuthHeaderComponent],
  template: `
    <main>
      <app-auth-header></app-auth-header>

      <div class="login-scroll">
      <div class="login-container">
      <h1 class="form-title">Anmelden</h1>
      
      <form [formGroup]="form" (ngSubmit)="onSubmit()">
        <div class="form-group">
          <label for="email">E-Mail:</label>
          <input
            id="email"
            type="email"
            formControlName="email"
            placeholder="E-Mail-Adresse"
          />
          @if (form.get('email')?.hasError('required') && form.get('email')?.touched) {
            <span class="error">E-Mail ist erforderlich</span>
          }
          @if (form.get('email')?.hasError('email') && form.get('email')?.touched) {
            <span class="error">Ungültige E-Mail</span>
          }
        </div>

        <div class="form-group">
          <label for="password">Passwort:</label>
          <div class="password-wrapper">
            <input
              id="password"
              [type]="showPassword() ? 'text' : 'password'"
              formControlName="password"
              placeholder="Passwort"
            />
            <button
              type="button"
              class="toggle-password-btn"
              (click)="showPassword.set(!showPassword())"
              tabindex="-1"
              [attr.aria-label]="showPassword() ? 'Passwort verbergen' : 'Passwort anzeigen'"
            >
              {{ showPassword() ? '👁️‍🗨️' : '👁️' }}
            </button>
          </div>
          @if (form.get('password')?.hasError('required') && form.get('password')?.touched) {
            <span class="error">Passwort ist erforderlich</span>
          }
        </div>

        @if (authService.errorMessage()) {
          <div class="error-message">
            {{ authService.errorMessage() }}
          </div>
          @if (authService.errorCode() === 'EMAIL_NOT_VERIFIED') {
            <button 
              type="button"
              (click)="onResendCode()"
              [disabled]="isResending() || !form.get('email')?.value"
              class="resend-button"
            >
              @if (isResending()) {
                Code wird versendet...
              } @else {
                Neuen Code versenden
              }
            </button>
          }
        }

        <button type="submit" [disabled]="!form.valid || isLoading()">
          @if (isLoading()) {
            Wird angemeldet...
          } @else {
            Anmelden
          }
        </button>
      </form>

      <p class="register-link">
        Noch kein Konto?
        <a routerLink="/register">Hier registrieren</a>
      </p>

      <p class="register-link">
        Passwort vergessen?
        <a routerLink="/forgot-password" class="forgot-password-link">Passwort zurücksetzen</a>
      </p>
      </div>
      </div>
    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0;
      min-height: 0;
      box-sizing: border-box;
    }

    .login-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      padding: 22px 2rem 1.5rem;
      box-sizing: border-box;
    }

    main > div:not(.header-container) {
      padding: 0 2rem;
    }

    main > div:first-child {
      padding: 0;
    }

    .login-container {
      max-width: 480px !important;
      margin: auto;
      padding: 40px;
      border: 1px solid #ccc;
      border-radius: 8px;
      background: #f9f9f9;
      width: 100%;
      box-sizing: border-box;
    }

    @media (max-height: 750px) {
      .login-container {
        margin: auto;
        padding: 30px;
      }
    }

    h1 {
      text-align: center;
      padding-top: 5px;
      margin-bottom: 22px;
      color: #333;
    }

    .form-group {
      margin-bottom: 17px;
      display: flex;
      flex-direction: column;
    }

    label {
      margin-bottom: 5px;
      color: #555;
      font-weight: 500;
    }

    input {
      padding: 14px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 15px;
      font-family: inherit;
      height: 45px;
      line-height: 1.5;
    }

    input:focus {
      outline: none;
      border-color: #4CAF50;
      box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
    }

    .password-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }

    .password-wrapper input {
      width: 100%;
      padding-right: 35px;
    }

    /* Browser-native Password Reveal Button verstecken */
    .password-wrapper input::-webkit-credentials-auto-fill-button,
    .password-wrapper input::-webkit-outer-spin-button,
    .password-wrapper input::-webkit-inner-spin-button {
      display: none;
    }

    .password-wrapper input::-ms-reveal {
      display: none;
    }

    .toggle-password-btn {
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      cursor: pointer;
      font-size: 18px;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      margin-top: -2px;
    }

    .toggle-password-btn:hover {
      background-color: white;
      border-radius: 4px;
    }

    .toggle-password-btn:focus {
      outline: none;
    }

    .error {
      color: #d32f2f;
      font-size: 12px;
      margin-top: 5px;
    }

    .error-message {
      background-color: #ffebee;
      color: #d32f2f;
      padding: 10px;
      border-radius: 4px;
      margin-bottom: 10px;
      border-left: 4px solid #d32f2f;
    }

    button {
      width: 100%;
      padding: 10px;
      background-color: #4CAF50;
      color: white;
      border: none;
      border-radius: 4px;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      transition: background-color 0.3s;
      margin-bottom: 15px;
    }

    button:hover:not(:disabled):not(.toggle-password-btn) {
      background-color: #45a049;
    }

    button:disabled {
      background-color: #cccccc;
      cursor: not-allowed;
    }

    .register-link {
      text-align: center;
      margin-top: 5px;
      font-size: 14px;
    }

    .register-link a {
      color: #4CAF50;
      text-decoration: none;
      font-weight: 500;
    }

    .register-link a:hover {
      text-decoration: underline;
    }

    .forgot-password-link {
      color: #0d47a1 !important;
      font-weight: normal !important;
      text-decoration: none;
    }

    .forgot-password-link:hover {
      text-decoration: underline;
    }

    .resend-button {
      background-color: #2196F3;
      font-size: 14px;
      margin-top: 0 !important;
    }

    .resend-button:hover:not(:disabled) {
      background-color: #0b7dda;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  protected authService = inject(AuthService);
  isLoading = signal(false);
  isResending = signal(false);
  showPassword = signal(false);

  form = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]]
  });

  constructor() {
    // Lösche alte Fehlermeldungen von anderen Seiten
    this.authService.errorMessage.set(null);
  }

  onSubmit() {
    if (this.form.valid) {
      const { email, password } = this.form.value;
      if (email && password) {
        this.isLoading.set(true);
        this.authService.login(email, password).subscribe({
          next: () => {
            this.isLoading.set(false);
            this.router.navigate(['/calculator']);
          },
          error: (err) => {
            this.isLoading.set(false);
            console.error('Login error:', err);
          }
        });
      }
    }
  }

  onResendCode() {
    const email = this.form.get('email')?.value;
    const password = this.form.get('password')?.value;
    if (!email || !password) return;

    this.isResending.set(true);
    this.authService.resendVerificationCode(email).subscribe({
      next: () => {
        this.isResending.set(false);
        // Passwort speichern für die Verifikation
        sessionStorage.setItem('registrationPassword', password);
        this.router.navigate(['/verify-email'], {
          queryParams: { email }
        });
      },
      error: (err) => {
        this.isResending.set(false);
        console.error('Resend error:', err);
      }
    });
  }
}
